import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
} from '@angular/core';
import {
  ScoreChipComponent,
  TagComponent,
  TagVariant,
  resolveScoreChipLevel,
} from '@catch/general-ui';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import {
  faArrowRightArrowLeft,
  faGear,
  faSort,
  faSortDown,
  faSortUp,
  faSparkles,
  faUser,
} from '@fortawesome/pro-light-svg-icons';
import { TranslocoPipe } from '@jsverse/transloco';
import { TableModule } from 'primeng/table';
import { PlatformJobApplyMenuComponent } from '../../../../components/job-apply-menu/platform-job-apply-menu.component';
import { PlatformJobBookmarkButtonComponent } from '../../../../components/job-bookmark-button/platform-job-bookmark-button.component';
import { PlatformCompanyLogoComponent } from '../../../../components/company-logo/platform-company-logo.component';
import { PlatformBoardJob } from '../../../../models/platform.models';
import { PlatformJobsService } from '../../../../services/platform-jobs.service';
import { PlatformSettingsService } from '../../../../services/platform-settings.service';
import { PlatformJobsEmptyStateComponent } from '../../../jobs/components/jobs-empty-state/platform-jobs-empty-state.component';

@Component({
  selector: 'app-platform-recommendations-list',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ScoreChipComponent,
    TagComponent,
    FontAwesomeModule,
    TranslocoPipe,
    TableModule,
    PlatformCompanyLogoComponent,
    PlatformJobApplyMenuComponent,
    PlatformJobBookmarkButtonComponent,
    PlatformJobsEmptyStateComponent,
  ],
  templateUrl: './platform-recommendations-list.component.html',
  styleUrl: './platform-recommendations-list.component.scss',
})
export class PlatformRecommendationsListComponent {
  readonly jobsService = inject(PlatformJobsService);
  private readonly settings = inject(PlatformSettingsService);

  readonly TagVariant = TagVariant;
  readonly icons = {
    sparkles: faSparkles,
    user: faUser,
    gear: faGear,
    switch: faArrowRightArrowLeft,
    sort: faSort,
    sortUp: faSortUp,
    sortDown: faSortDown,
  };

  readonly paused = computed(() => this.settings.recommendationsPaused());

  readonly jobs = computed(() => this.jobsService.recommendedJobs());

  /**
   * Tracks table rows by board job id.
   * @param _index - Row index
   * @param job - Board job
   * @returns Stable row id
   */
  trackById(_index: number, job: PlatformBoardJob): number {
    return job.id;
  }

  /**
   * Score chip label key for a job.
   * @param job - Board job
   * @returns Translation key
   */
  scoreLabelKey(job: PlatformBoardJob): string {
    if (job.score == null) {
      return '';
    }
    return `jobboard.platform.recommendations.score.${resolveScoreChipLevel(job.score)}`;
  }

  /**
   * Working-model label key for a job.
   * @param job - Board job
   * @returns Translation key
   */
  workingModelLabelKey(job: PlatformBoardJob): string {
    return `jobboard.platform.profile.working-model.${job.workingModel}`;
  }
}
